import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShoppingBag, User, LogOut, LayoutDashboard, Settings } from 'lucide-react';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === '/';
  const isLoggedIn = !!user;
  const isDark = isHome && !isLoggedIn;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const goToDashboard = () => {
    if (user?.role === 'admin') {
      navigate('/admin/dashboard');
    } else if (user?.role === 'seller') {
      navigate('/seller/dashboard');
    } else {
      navigate('/');
    }
  };

  return (
    <nav className={`sticky top-0 z-50 transition-colors duration-300 ${
      isDark
        ? 'bg-zinc-950/70 backdrop-blur-xl border-b border-white/10'
        : 'bg-white border-b border-gray-200 shadow-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          <div 
            className="flex items-center space-x-3 cursor-pointer"
            onClick={() => navigate('/')}
            data-testid="navbar-logo"
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              isDark ? 'bg-gradient-to-br from-blue-600 to-cyan-500' : 'bg-gradient-to-br from-indigo-600 to-purple-600'
            }`}>
              <ShoppingBag className="w-6 h-6 text-white" />
            </div>
            <span className={`text-2xl font-bold ${
              isDark ? 'text-white' : 'bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent'
            }`} style={isDark ? { fontFamily: 'Outfit, sans-serif' } : {}}>
              {user?.store_name || 'Needi'}
            </span>
          </div>

          <div className="flex items-center space-x-4">
            {user ? (
              <>
                <button
                  onClick={goToDashboard}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    isDark ? 'hover:bg-white/10 text-zinc-300' : 'hover:bg-indigo-50'
                  }`}
                  data-testid="navbar-dashboard-btn"
                >
                  <LayoutDashboard className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-indigo-600'}`} />
                  <span className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>Dashboard</span>
                </button>
                <button
                  onClick={() => navigate('/settings')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    isDark ? 'hover:bg-white/10 text-zinc-300' : 'hover:bg-indigo-50'
                  }`}
                  data-testid="navbar-settings-btn"
                >
                  <Settings className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-indigo-600'}`} />
                  <span className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>Configurações</span>
                </button>
                <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                  isDark ? 'bg-white/10' : 'bg-indigo-50'
                }`}>
                  <User className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-indigo-600'}`} />
                  <span className={`text-sm font-medium ${isDark ? 'text-zinc-200' : 'text-gray-700'}`}>{user.name}</span>
                </div>
                <button
                  onClick={handleLogout}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    isDark ? 'hover:bg-red-500/20' : 'hover:bg-red-50'
                  }`}
                  data-testid="navbar-logout-btn"
                >
                  <LogOut className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-medium text-red-500">Sair</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/login')}
                  className={`px-6 py-2 font-medium rounded-lg transition-colors ${
                    isDark ? 'text-zinc-300 hover:bg-white/10' : 'text-indigo-600 hover:bg-indigo-50'
                  }`}
                  data-testid="navbar-login-btn"
                >
                  Entrar
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className={`px-6 py-2.5 font-medium rounded-full transition-all ${
                    isDark
                      ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_15px_rgba(37,99,235,0.3)]'
                      : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-md hover:shadow-lg rounded-lg'
                  }`}
                  data-testid="navbar-register-btn"
                >
                  Cadastrar
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};
